/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { IconButton, List } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { environment } from "../../environment";
import {
    ArrowLeftIcon,
    BanIcon,
    BarcodeReadIcon,
    BarsIcon,
    BooksIcon,
    BoxCircleCheckIcon,
    BuildingIcon,
    CashRegisterIcon,
    ChartBulletIcon,
    ChartGanttIcon,
    ChartSimpleIcon,
    CircleUserIcon,
    ClipboardQuestionIcon,
    CoinsIcon,
    EMailIcon,
    FileCertificateIcon,
    FileImportIcon,
    FileInvoiceIcon,
    FileSpreadSheetIcon,
    ForkKnifeIcon,
    GearIcon,
    GlobeIcon,
    HandHoldingDollarIcon,
    HandshakeIcon,
    HouseLaptopIcon,
    InboxOutIcon,
    IndustryIcon,
    ListOlIcon,
    LocationDotIcon,
    MoneyBillIcon,
    PayPalIcon,
    PenPaintbrushIcon,
    PeopleGroupIcon,
    PeopleRoofIcon,
    PersonChalkboardIcon,
    PhoneIcon,
    PrintIcon,
    ShoppingBagIcon,
    StripeIcon,
    SymbolsIcon,
    TagsIcon,
    TaskIcon,
    UserGroupIcon,
    UsersBetweenLinesIcon,
    UsersClassIcon,
    UsersGearIcon,
    UsersIcon,
    UtensilsIcon,
    WheatAwnCircleExclamationIcon
} from "../../FontAwesome";
import useErmittleBuchungsportalUrl from "../../Utils/useErmittleBuchungsportalUrl";
import useNavigate from "../../Utils/useNavigate";
import WindowUtil from "../../Utils/WindowUtil";
import { useAppMenuContext } from "../AppMenuContext";
import { useAppRollenContext } from "../AppRollenContext";
import { translationKeys, useTranslation } from "./../../Internationalisierung/i18nBasis";
import AppMenuHeaderItem from "./../AppMenuHeaderItem";
import AppMenuItem from "./../AppMenuItem";
import "./AppMenu.scss";


export default function AppMenu() {
    const { t } = useTranslation();
    const appRollenContext = useAppRollenContext();
    const appMenuContext = useAppMenuContext();
    const navigate = useNavigate();
    const ermittleBuchungsportalUrl = useErmittleBuchungsportalUrl();

    function navigiereZu(path: string) {
        navigate(path, { replace: false, enableChangeTracking: true });
        appMenuContext.schliesse();
    }

    const hauptMenu = (
        <List className="lan-appmenu__list">
            <AppMenuItem
                onClick={() => navigiereZu("/seminare")}
                icon={<UsersIcon />}
                text={t(translationKeys.appMenu.veranstaltungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/seminarreihen")}
                icon={<UsersBetweenLinesIcon />}
                text={t(translationKeys.appMenu.veranstaltungsreihen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/bestellungen")}
                icon={<BoxCircleCheckIcon />}
                text={t(translationKeys.appMenu.bestellungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/teilnahmen")}
                icon={<UsersClassIcon />}
                text={t(translationKeys.appMenu.teilnahmen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/adressen")}
                icon={<PeopleGroupIcon />}
                text={t(translationKeys.appMenu.adressen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/dozierende")}
                icon={<PersonChalkboardIcon />}
                text={t(translationKeys.appMenu.dozierende)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/artikelsammlung")}
                icon={<ShoppingBagIcon />}
                text={t(translationKeys.appMenu.artikel)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/bestellteArtikel")}
                icon={<CashRegisterIcon />}
                text={t(translationKeys.appMenu.bestellteArtikel)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/belege")}
                icon={<FileInvoiceIcon />}
                text={t(translationKeys.appMenu.belege)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/belegungen")}
                icon={<HouseLaptopIcon />}
                text={t(translationKeys.appMenu.belegungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/zahlungen")}
                icon={<CoinsIcon />}
                text={t(translationKeys.appMenu.zahlungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/fibuExporte")}
                icon={<HandHoldingDollarIcon />}
                text={t(translationKeys.appMenu.fibuExporte)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/auswertungen")}
                icon={<ChartSimpleIcon />}
                text={t(translationKeys.appMenu.auswertungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ressourcenplan")}
                icon={<ChartGanttIcon />}
                text={t(translationKeys.appMenu.ressourcenplan)}
            />
            <AppMenuItem
                onClick={() => WindowUtil.oeffneInTab(ermittleBuchungsportalUrl())}
                icon={<GlobeIcon />}
                text={t(translationKeys.appMenu.buchungsportal)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ticketscanner")}
                icon={<BarcodeReadIcon />}
                text={t(translationKeys.appMenu.ticketscanner)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/importe")}
                icon={<FileImportIcon />}
                text={t(translationKeys.appMenu.importe)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/postausgang")}
                icon={<InboxOutIcon />}
                text={t(translationKeys.appMenu.postausgang)}
            />

            {appRollenContext.hatGrundeinstellungenRecht && <>
                <AppMenuHeaderItem text={t(translationKeys.appMenu.einstellungen)} />
                <AppMenuItem
                    icon={<GearIcon />}
                    onClick={() => appMenuContext.zeigeGrundeinstellungenMenu()}
                    text={t(translationKeys.appMenu.grundeinstellungen)}
                />
            </>}

            <AppMenuHeaderItem text={t(translationKeys.appMenu.programmierschnittstelle)} />
            <AppMenuItem
                icon={<BooksIcon />}
                href={`/${environment.mandant}/restapi/swagger`}
                text={t(translationKeys.appMenu.dokumentation)}
            />

            {appRollenContext.istSupport && (
                <>
                    <AppMenuHeaderItem text={t(translationKeys.appMenu.supporteinstellungen)} />
                    <AppMenuItem
                        icon={<PhoneIcon />}
                        onClick={() => appMenuContext.zeigeSupporteinstellungenMenu()}
                        text={t(translationKeys.appMenu.supporteinstellungen)}
                    />
                </>
            )}
        </List>
    );

    const grundeinstellungenMenu = (
        <List className="lan-appmenu__list">
            <AppMenuItem
                icon={<ArrowLeftIcon />}
                onClick={() => appMenuContext.zeigeHauptMenu()}
                text={t(translationKeys.appMenu.grundeinstellungenVerlassen)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.branding)} />
            <AppMenuItem
                onClick={() => navigiereZu("/branding")}
                icon={<PenPaintbrushIcon />}
                text={t(translationKeys.appMenu.branding)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/branding/eingabefelderDefinitionen")}
                icon={<TaskIcon />}
                text={t(translationKeys.appMenu.definitionen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/branding/aushang")}
                icon={<HouseLaptopIcon />}
                text={t(translationKeys.appMenu.aushang)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.zusatzfelder)} />
            <AppMenuItem
                onClick={() => navigiereZu("/zusatzfelder/seminar")}
                icon={<ChartBulletIcon />}
                text={t(translationKeys.appMenu.zusatzfelder)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.preise)} />
            <AppMenuItem
                onClick={() => navigiereZu("/preise")}
                icon={<MoneyBillIcon />}
                text={t(translationKeys.appMenu.preisstamm)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/rabattcodes")}
                icon={<TagsIcon />}
                text={t(translationKeys.appMenu.rabattcodes)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.darstellung)} />
            <AppMenuItem
                onClick={() => navigiereZu("/konfigurierbareListenansichten")}
                icon={<PenPaintbrushIcon />}
                text={t(translationKeys.appMenu.anpassungenFuerListenansichten)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.belege)} />
            <AppMenuItem
                onClick={() => navigiereZu("/finanzeinstellungen")}
                icon={<GearIcon />}
                text={t(translationKeys.appMenu.finanzeinstellungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/nummernkreise")}
                icon={<ListOlIcon />}
                text={t(translationKeys.appMenu.nummernkreise)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/stornobedingungen")}
                icon={<BanIcon />}
                text={t(translationKeys.appMenu.stornobedingungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/fibuExportEinstellungen")}
                icon={<HandHoldingDollarIcon />}
                text={t(translationKeys.appMenu.fibuExportEinstellungen)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.mitarbeiter)} />
            <AppMenuItem
                onClick={() => navigiereZu("/mitarbeiterRollen")}
                icon={<UsersGearIcon />}
                text={t(translationKeys.appMenu.mitarbeiterRollen)}
            />

            <AppMenuHeaderItem text={t(translationKeys.appMenu.veranstaltungen)} />
            <AppMenuItem
                onClick={() => navigiereZu("/seminareinstellungen")}
                icon={<UsersIcon />}
                text={t(translationKeys.appMenu.veranstaltungseinstellungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/gruppenveranstaltungEinstellungen")}
                icon={<PeopleRoofIcon />}
                text={t(translationKeys.appMenu.gruppenveranstaltungEinstellungen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/kategorien")}
                icon={<SymbolsIcon />}
                text={t(translationKeys.appMenu.kategorien)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/orte")}
                icon={<LocationDotIcon />}
                text={t(translationKeys.appMenu.orte)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/frageboegen")}
                icon={<ClipboardQuestionIcon />}
                text={t(translationKeys.appMenu.frageboegen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/kooperationspartner")}
                icon={<HandshakeIcon />}
                text={t(translationKeys.appMenu.kooperationspartner)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/zertifikate")}
                icon={<FileCertificateIcon />}
                text={t(translationKeys.appMenu.zertifikate)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.artikel)} />
            <AppMenuItem
                onClick={() => navigiereZu("/artikeleinstellungen")}
                icon={<GearIcon />}
                text={t(translationKeys.appMenu.artikeleinstellungen)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.adressen)} />
            <AppMenuItem
                onClick={() => navigiereZu("/adresskategorien")}
                icon={<SymbolsIcon />}
                text={t(translationKeys.appMenu.adresskategorien)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.dozierende)} />
            <AppMenuItem
                onClick={() => navigiereZu("/dozierendKategorien")}
                icon={<SymbolsIcon />}
                text={t(translationKeys.appMenu.dozierendKategorien)}
            />
            <AppMenuHeaderItem
                text={t(translationKeys.appMenu.ressourcen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ressourcen")}
                icon={<HouseLaptopIcon />}
                text={t(translationKeys.appMenu.ressourcen)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ressourcenkategorien")}
                icon={<SymbolsIcon />}
                text={t(translationKeys.appMenu.ressourcenkategorien)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ressourceneinstellungen")}
                icon={<GearIcon />}
                text={t(translationKeys.appMenu.ressourcenEinstellungen)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.mahlzeiten)} />
            <AppMenuItem
                onClick={() => navigiereZu("/mahlzeiten")}
                icon={<UtensilsIcon />}
                text={t(translationKeys.appMenu.mahlzeiten)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/verpflegungsarten")}
                icon={<ForkKnifeIcon />}
                text={t(translationKeys.appMenu.verpflegungsarten)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/ernaehrungsformen")}
                icon={<WheatAwnCircleExclamationIcon />}
                text={t(translationKeys.appMenu.ernaehrungsformenAllergien)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.mandant)} />
            <AppMenuItem
                onClick={() => navigiereZu("/mandant")}
                icon={<BuildingIcon />}
                text={t(translationKeys.appMenu.mandant)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/mandant/firmendaten")}
                icon={<IndustryIcon />}
                text={t(translationKeys.appMenu.firmendaten)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/nutzende")}
                icon={<UserGroupIcon />}
                text={t(translationKeys.appMenu.nutzende)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/mandantBelege")}
                icon={<FileInvoiceIcon />}
                text={t(translationKeys.appMenu.mandantBelege)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.eMails)} />
            <AppMenuItem
                onClick={() => navigiereZu("/emailprofile")}
                icon={<EMailIcon />}
                text={t(translationKeys.appMenu.eMailprofile)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/emailtexte")}
                icon={<EMailIcon />}
                text={t(translationKeys.appMenu.eMailTexte)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/emaileinstellungen")}
                icon={<GearIcon />}
                text={t(translationKeys.appMenu.eMailEinstellungen)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.exporte)} />
            <AppMenuItem
                onClick={() => navigiereZu("/exportvorlagen")}
                icon={<FileSpreadSheetIcon />}
                text={t(translationKeys.appMenu.exportvorlagen)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.integration)} />
            <AppMenuItem
                onClick={() => navigiereZu("/integration/teams")}
                icon={<UsersIcon />}
                text={t(translationKeys.appMenu.teams)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/integration/zoom")}
                icon={<UsersIcon />}
                text={t(translationKeys.appMenu.zoom)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/zahlungsdienstleister/stripe")}
                icon={<StripeIcon />}
                text={t(translationKeys.appMenu.stripe)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/zahlungsdienstleister/paypal")}
                icon={<PayPalIcon />}
                text={t(translationKeys.appMenu.paypal)}
            />
        </List>
    );

    const supporteinstellungenMenu = (
        <List className="lan-appmenu__list">
            <AppMenuItem
                icon={<ArrowLeftIcon />}
                onClick={() => appMenuContext.zeigeHauptMenu()}
                text={t(translationKeys.appMenu.supporteinstellungenVerlassen)}
            />
            <AppMenuHeaderItem text={t(translationKeys.appMenu.support)} />
            <AppMenuItem
                onClick={() => navigiereZu("/supportmandanten")}
                icon={<CircleUserIcon />}
                text={t(translationKeys.appMenu.supportmandanten)}
            />
            <AppMenuItem
                onClick={() => navigiereZu("/mitteilungen")}
                icon={<EMailIcon />}
                text={t(translationKeys.appMenu.mitteilungen)}
            />
            <AppMenuItem
                icon={<InboxOutIcon />}
                onClick={() => navigiereZu("/systemPostausgang")}
                text={t(translationKeys.appMenu.postausgang)}
            />
            {appRollenContext.istAdministration && (
                <>
                    <AppMenuItem
                        icon={<PrintIcon />}
                        onClick={() => navigiereZu("/belegdruckSupport")}
                        text={t(translationKeys.appMenu.belegdruck)}
                    />
                    <AppMenuItem
                        icon={<FileInvoiceIcon />}
                        onClick={() => navigiereZu("/reportdesigner")}
                        text={t(translationKeys.appMenu.reportdesigner)}
                    />
                    <AppMenuItem
                        icon={<GlobeIcon />}
                        href={`/${environment.mandant}/api`}
                        text={t(translationKeys.appMenu.api)}
                    />
                </>
            )}
        </List>
    );

    return <>
        <IconButton
            className="lan-appmenu__button"
            color="inherit"
            onClick={() => appMenuContext.oeffne()}
            size="large"
            aria-label={t(translationKeys.appMenu.menuButtonLabel)}
            title={t(translationKeys.appMenu.menuButtonTitel)}>
            <BarsIcon />
        </IconButton>
        <Drawer
            className="lan-appmenu__drawer"
            anchor="left"
            classes={{ paper: "lan-appmenu__drawer__paper" }}
            open={appMenuContext.isOpen}
            onClose={() => appMenuContext.schliesse()}
        >
            {appMenuContext.hauptMenuSichtbar && hauptMenu}
            {appMenuContext.grundeinstellungenMenuSichtbar && grundeinstellungenMenu}
            {appMenuContext.supporteinstellungenMenuSichtbar &&
                appRollenContext.istSupport &&
                supporteinstellungenMenu}
        </Drawer>
    </>;
}